const businessContent = {
  title: '비즈니스 관리 매뉴얼',
  content: `
  <div class="page-title">[비즈니스 관리]</div>

# [개요]
비즈니스 관리 기능은 웹사이트의 비즈니스 영역 콘텐츠를 효율적으로 관리하기 위한 도구입니다. 이 문서에서는 **비즈니스 콘텐츠의 등록, 수정, 순서 변경 등 전반적인 관리 방법**을 확인할 수 있습니다.

### 주요 기능 요약
| 기능 | 설명 | 권한 |
|------|------------------|------------|
| 비즈니스 조회 | 등록된 비즈니스 목록 확인 및 검색 | 관리자 |
| 비즈니스 등록 | 신규 비즈니스 콘텐츠 생성 및 설정 | 관리자 |
| 비즈니스 수정 | 기존 비즈니스 정보 수정 | 관리자 |
| 순서 변경 | 비즈니스 콘텐츠 노출 순서 관리 | 관리자 |

# [조회]
등록된 비즈니스 콘텐츠의 목록을 조회하고 관리할 수 있습니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 검색 영역 | 검색 조건 및 필터 | • 타입/언어별 필터링<br>• 제목 기준 검색 |
| 목록 영역 | 비즈니스 목록 테이블 | • 타입/상태/사용여부 확인<br>• 순서 확인 및 변경<br>• 미리보기 이미지 확인 |
| 버튼 영역 | 카테고리 버튼<br>순서 변경 버튼<br>등록 버튼 | • 카테고리 관리 화면으로 이동<br>• 순서 변경 화면으로 이동<br>• 신규 비즈니스 등록 |

![비즈니스 목록](/assets/images/guide/business/list.png)

# [카테고리 화면으로 이동]
그리드 우측 상단의 '카테고리' 버튼 선택 시 카테고리 관리 화면으로 이동합니다.

# [순서변경]
그리드 좌측 상단의 '순서 변경' 버튼 선택 시 비즈니스와 카테고리의 순서를 관리하고 카테고리 간 이동이 가능합니다.

### 📋 화면 구성 및 기능
| 영역 | 구성 | 주요 기능 |
|------|------|-----------|
| 언어 선택 | 언어 필터 | • 언어별 콘텐츠 분리 관리<br>• 동일 언어 내 순서 변경 |
| 순서 관리 | 드래그 앤 드롭 영역 | • 비즈니스 순서 변경<br>• 카테고리 간 이동<br>• 실시간 순서 저장 |
| 카테고리 영역 | 카테고리 목록 | • 카테고리 순서 변경<br>• 파이프라인 상태 확인 |
| 사용 여부 | 사용 여부 필터 | • 사용 여부 필터링 |

> 💡 **Tip**
> - 드래그 앤 드롭으로 순서 변경과 카테고리 이동이 가능합니다.
> - 같은 언어 설정 내에서만 순서 변경이 가능합니다.
> - 순서 변경 시 실시간으로 반영됩니다.

> ⚠️ **주의사항**
> - 파이프라인이 설정된 비즈니스를 파이프라인이 없는 카테고리로 이동하면 이동된 파이프라인 데이터가 삭제됩니다.
> - 카테고리 이동 시 연관 데이터가 삭제될 수 있으니 신중하게 이동해 주세요.

<div class="image-row">
  <div class="image-col">
    <img src="/assets/images/guide/business/orderChange_ko.png" alt="비즈니스 순서관리(한국어)" />
  </div>
  <div class="image-col">
    <img src="/assets/images/guide/business/orderChange_en.png" alt="비즈니스 순서관리(영어)" />
  </div>
</div>
*비즈니스 순서관리(한국어)와 비즈니스 순서관리(영어) 비교*

# [등록/수정]
비즈니스 콘텐츠를 신규 등록하거나 기존 정보를 수정할 수 있습니다.

> ⚠️ **주의사항**
> - 이미지는 데스크탑용과 모바일용을 각각 업로드해야 합니다.

### 📋 화면 구성 및 기능
| 영역 | 입력 항목 | 설명 |
|------|-----------|------|
| 기본 설정 | • 사용 여부<br>• 언어<br>• 카테고리(카테고리에 따라 파이프라인 추가) | • 활성화 상태 설정<br>• 노출 언어 선택<br>• 비즈니스 타입 설정<br> |
| 콘텐츠 설정 | • 제목<br>• 요약<br>• 내용<br>• 이미지(데스크탑용, 모바일용) | • 비즈니스 제목 입력<br>• 상세 내용 입력<br>• 대표 이미지 업로드<br>• 메인 컨텐츠로 1개 등록 |
| 서브 콘텐츠 설정 | • 제목<br>• 내용<br>• 이미지(데스크탑용, 모바일용) | • 비즈니스 제목 입력<br>• 상세 내용 입력<br>• 이미지 업로드<br>• 필요한만큼 추가 가능 |

![비즈니스 수정01](/assets/images/guide/business/edit01.png)
![비즈니스 수정02](/assets/images/guide/business/edit02.png)

### 🎨 타입별 스타일 가이드
카테고리의 타입 설정에 따라 비즈니스의 레이아웃이 결정됩니다.

| 카테고리 타입 | 특징 | 비즈니스 레이아웃 | 필수 항목 |
|------------|------|-----------------|-----------|
| TYPE_A | • 탭 형태로 노출<br>• 비즈니스 간 전환 가능 | • 메인 컨텐츠, 서브 컨텐츠 순차적으로 노출 | • 메인 컨텐츠의 title, descript, 이미지<br>• 서브 컨텐츠의 title, descript, 이미지 |
| TYPE_B | • 리스트 형태로 노출<br>• 순차적 스크롤 | • 메인 컨텐츠, 서브 컨텐츠 순차적으로 노출 | • 메인 컨텐츠의 title, 이미지<br>• 서브 컨텐츠의 title, 이미지 |

> ⚠️ **주의사항**
> - 비즈니스 등록 시 상위 카테고리의 타입을 반드시 확인하세요.
> - 카테고리 타입에 맞는 레이아웃으로 자동 적용됩니다.
> - 카테고리 타입이 변경되면 하위 비즈니스의 레이아웃도 함께 변경됩니다.

<div class="image-row">
  <div class="image-col">
    <img src="/assets/images/guide/business/typeA.jpeg" alt="TYPE A 카테고리와 비즈니스" />
  </div>
  <div class="image-col">
    <img src="/assets/images/guide/business/typeB.jpeg" alt="TYPE B 카테고리와 비즈니스" />
  </div>
</div>

*TYPE A 탭 형태(좌)와 TYPE B 리스트 형태(우) 레이아웃 비교*
    `,
};
export default businessContent;
